import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import styles from "./TransactionMetrics.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, Filler);

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const F = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parseFloat(v) || 0);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const COLORS = ["#667eea", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4"];

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { display: false }, ticks: { color: "var(--text-muted)", font: { size: 10 } } },
    y: { grid: { color: "var(--border)", drawBorder: false }, ticks: { color: "var(--text-muted)", font: { size: 10 }, callback: (v) => F(v) } },
  },
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: { legend: { display: false } },
};

/* ------------------------------------------------------------------ */
/*  Skeleton                                                          */
/* ------------------------------------------------------------------ */

const Skeleton = () => (
  <div className={styles.grid}>
    <div className={styles.statCard}>
      <div className={`${styles.skel}`} style={{ height: 14, width: 80, marginBottom: 12 }} />
      <div className={styles.statRow}>
        {[1, 2, 3].map((i) => (
          <div key={i} className={styles.statPill}>
            <div className={`${styles.skel}`} style={{ height: 24, width: 50, margin: "0 auto 4px" }} />
            <div className={`${styles.skel}`} style={{ height: 10, width: 40, margin: "0 auto" }} />
          </div>
        ))}
      </div>
      <div className={`${styles.skel}`} style={{ height: 160 }} />
    </div>
    <div className={styles.statCard}>
      <div className={`${styles.skel}`} style={{ height: 14, width: 80, marginBottom: 12 }} />
      <div style={{ display: "flex", gap: 16 }}>
        <div className={`${styles.skel}`} style={{ width: 120, height: 120, borderRadius: "50%" }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`${styles.skel}`} style={{ height: 12 }} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

export default function TransactionMetrics({ transactions = [], loading = false }) {
  const stats = useMemo(() => {
    if (!transactions.length) return null;

    const txs = [...transactions];
    const total = txs.length;
    const totalAmount = txs.reduce((s, t) => s + parseFloat(t.totalFee || 0), 0);
    const avgSize = totalAmount / total;
    // Monthly spending (last 6 months)
    const today = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({ label: MONTHS[d.getMonth()], key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, total: 0 });
    }
    txs.forEach((t) => {
      const d = new Date(t.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const m = months.find((m) => m.key === key);
      if (m) m.total += parseFloat(t.totalFee || 0);
    });
    const currentMonth = months[months.length - 1].total;
    const prevMonth = months[months.length - 2].total;
    const monthTrend = prevMonth > 0 ? ((currentMonth - prevMonth) / prevMonth) * 100 : 0;

    // Payment method breakdown
    const methodMap = {};
    txs.forEach((t) => {
      const m = t.paymentMethod || "Unknown";
      methodMap[m] = (methodMap[m] || 0) + parseFloat(t.totalFee || 0);
    });
    const sortedMethods = Object.entries(methodMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return { total, avgSize, months, currentMonth, prevMonth, monthTrend, sortedMethods };
  }, [transactions]);

  if (loading) return <Skeleton />;
  if (!stats || !transactions.length) {
    return (
      <div className={styles.grid} style={{ gridTemplateColumns: "1fr" }}>
        <div className={styles.statCard}>
          <div className={styles.empty}>No transaction data yet. Start transacting to see analytics.</div>
        </div>
      </div>
    );
  }

  const barData = {
    labels: stats.months.map((m) => m.label),
    datasets: [
      {
        data: stats.months.map((m) => m.total),
        backgroundColor: "rgba(102,126,234,0.25)",
        borderColor: "#667eea",
        borderWidth: 1.5,
        borderRadius: 6,
        fill: true,
      },
    ],
  };

  const doughnutData = {
    labels: stats.sortedMethods.map(([name]) => name),
    datasets: [
      {
        data: stats.sortedMethods.map(([, val]) => val),
        backgroundColor: COLORS.slice(0, stats.sortedMethods.length),
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className={styles.grid}>
      {/* Card 1 — Stat pills + Bar */}
      <div className={styles.statCard}>
        <div className={styles.statHeader}>Transaction Overview</div>
        <div className={styles.statRow}>
          <div className={styles.statPill}>
            <div className={styles.statPillValue}>{stats.total}</div>
            <div className={styles.statPillLabel}>Total</div>
          </div>
          <div className={styles.statPill}>
            <div className={styles.statPillValue}>{F(stats.avgSize)}</div>
            <div className={styles.statPillLabel}>Avg Size</div>
          </div>
          <div className={styles.statPill}>
            <div className={styles.statPillValue}>
              {F(stats.currentMonth)}
              <span className={stats.monthTrend >= 0 ? styles.trendUp : styles.trendDown} style={{ fontSize: "0.7rem", marginLeft: 4 }}>
                {stats.monthTrend >= 0 ? "▲" : "▼"}
              </span>
            </div>
            <div className={styles.statPillLabel}>This Month</div>
          </div>
        </div>

        <div className={styles.barWrap}>
          <Bar data={barData} options={chartOptions} />
        </div>
      </div>

      {/* Card 2 — Doughnut + Legend */}
      <div className={styles.doughnutCard}>
        <div className={styles.statHeader}>Payment Methods</div>
        <div className={styles.doughnutRow}>
          <div className={styles.doughnutCanvas}>
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </div>
          <div className={styles.legend}>
            {stats.sortedMethods.map(([name, val], i) => (
              <div key={name} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: COLORS[i] }} />
                <span>{name}</span>
                <span className={styles.legendValue}>{F(val)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

TransactionMetrics.propTypes = {
  transactions: PropTypes.array,
  loading: PropTypes.bool,
};